import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from 'src/app/shared/services/api.service';

@Component({
    selector: 'molla-ai',
    templateUrl: './ai.component.html',
    styleUrls: ['./ai.component.scss']
})
export class AiComponent {

    inlinePrompt = '';

    upscaleFile: File | null = null;
    upscalePreviewUrl: string | null = null;
    isUpscaleDragOver = false;
    isUpscaling = false;

    constructor(
        private toastr: ToastrService,
        private apiService: ApiService,
        private router: Router
    ) {}

    private get currentUser(): any {
        try {
            return JSON.parse(localStorage.getItem('currentUser') || 'null');
        } catch {
            return null;
        }
    }

    private get customerFrom(): number {
        return this.currentUser?.isExclusive === true ? 2 : 1;
    }

    generateFromInline(): void {
        const text = (this.inlinePrompt || '').trim();
        if (!text) {
            return;
        }
        this.router.navigate(
            ['/exclusive/account/ai-chat-assistant'],
            { queryParams: { prompt: text } }
        );
    }

    openChatAssistant(): void {
        this.router.navigate(['/exclusive/account/ai-chat-assistant']);
    }

    onInlineKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.generateFromInline();
        }
    }

    onUpscaleDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isUpscaleDragOver = true;
    }

    onUpscaleDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isUpscaleDragOver = false;
    }

    onUpscaleDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isUpscaleDragOver = false;

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            this.handleUpscaleFile(files[0]);
        }
    }

    onUpscaleFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input?.files && input.files.length > 0) {
            this.handleUpscaleFile(input.files[0]);
            input.value = '';
        }
    }

    private handleUpscaleFile(file: File): void {
        const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            this.toastr.error('Only JPG, PNG and WEBP images are supported. Please upload a valid file format');
            return;
        }
        this.upscaleFile = file;
        const reader = new FileReader();
        reader.onload = (e: any) => {
            this.upscalePreviewUrl = e.target.result;
        };
        reader.readAsDataURL(file);
    }

    removeUpscaleImage(): void {
        this.upscaleFile = null;
        this.upscalePreviewUrl = null;
    }

    upscaleImage(tool: string): void {
        if (!this.currentUser) {
            this.toastr.error('Please purchase a plan to enable AI image creation');
            this.router.navigate(['/subscription']);
            return;
        }

        if (!this.upscaleFile) {
            this.toastr.error('No image selected for upscaling.');
            return;
        }

        const formData = new FormData();
        formData.append('image', this.upscaleFile);
        formData.append('tool', tool);
        formData.append('customer_from', String(this.customerFrom));
        formData.append('customer_id', String(this.currentUser?.customers_id || ''));

        this.isUpscaling = true;
        this.apiService.upscaleImage(formData).subscribe({
            next: (res: any) => {
                this.isUpscaling = false;
                this.toastr.success('Image upscaled successfully!');
                const a = document.createElement('a');
                a.href = res.image_url;
                a.setAttribute('download', 'upscaled_image.jpg');
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            },
            error: (err: any) => {
                this.isUpscaling = false;
                if (err?.error?.isPackageExpired === true) {
                    this.toastr.error('You’ve reached your usage limit. Please renew your plan to continue using this feature');
                } else {
                    this.toastr.error(
                        err?.error?.errors?.image_reference?.[0] ??
                        'We’re processing a lot of requests right now. Please try again shortly. Only JPG, PNG and WEBP images are supported.'
                    );
                }
            }
        });
    }
}
